import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import common from "../../utils/common";
import Analytics from "../../libs/analytics";
import ClientConfigurationHelper from "../../configuration/ClientConfigurationHelper";
import QuickAdd from "../QuickAdd/QuickAdd";

const REVEAL_STEP_MS = 35;
const MAX_STAGGERED_CARDS = 10;
const EAGER_CARD_COUNT = 3;

const trackedImpressions = new Set();

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const toAmount = (value) => {
    if (!hasValue(value)) return null;
    const numeric = typeof value === "number" ? value : Number(String(value).replace(/[^\d.]/g, ""));
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const ProductsCollectionItem = ({ item, userId, contentId, linkOnly = false, card = false, index = 0 }) => {
    const [loaded, setLoaded] = useState(false);
    const aboveFold = index < EAGER_CARD_COUNT;
    const revealDelay = aboveFold ? 0 : Math.min(index, MAX_STAGGERED_CARDS) * REVEAL_STEP_MS;
    const { title, description } = item;
    const brand = item.brand || item.data?.brand;
    const isLink = linkOnly || card;
    const image_url = item.image_url || item.data?.imageUrl || item.imageUrl || item.image;
    const link_url = item.link_url || item.data?.linkUrl || item.linkUrl || item.link;
    const price = toAmount(item.price ?? item.data?.price);
    const sale_price = toAmount(item.sale_price ?? item.price_sale ?? item.data?.price_sale);
    const product_id = item.product_id || item.id;
    const currency = ClientConfigurationHelper.getCurrencySymbol();
    const hasPrice = price !== null;
    const onSale = hasPrice && sale_price !== null && sale_price < price;
    const money = (value) => `${currency}${Number(value).toFixed(2)}`;

    const productUrl = common.addSharedIdToUrl(link_url, userId, product_id);
    const trackClick = () => Analytics.productClick({ ...item, id: product_id }, contentId);

    useEffect(() => {
        if (!contentId || !product_id) return;
        const key = `${contentId}:${product_id}`;
        if (trackedImpressions.has(key)) return;
        trackedImpressions.add(key);
        Analytics.productImpresion({ ...item, id: product_id }, contentId);
    }, [contentId, product_id]);

    return (
        <Box
            className="quickview-external-container"
            data-testid="tvp-product-card"
            data-itemid={product_id}
            sx={{ position: "relative" }}
        >
        <Box
            component={isLink ? "a" : "div"}
            href={isLink ? productUrl : undefined}
            target={isLink ? "_blank" : undefined}
            rel={isLink ? "noopener noreferrer" : undefined}
            onClick={isLink ? trackClick : undefined}
            sx={{
                display: "block",
                textDecoration: "none",
                backgroundColor: (theme) => theme.tvp.card,
                cursor: "pointer",
                ...(card
                    ? {
                          padding: "10px",
                          borderRadius: (theme) => theme.tvp.radiusCard,
                          boxShadow: (theme) => theme.tvp.shadowRest,
                          transition: "box-shadow 160ms ease",
                          "&:hover": { boxShadow: (theme) => theme.tvp.cardHover },
                      }
                    : {}),
                "&:hover .tvp-product-media": { transform: "scale(1.05)" },
                ...(linkOnly || card
                    ? {}
                    : {
                          transition: "box-shadow 160ms ease, transform 160ms ease",
                          "&:hover": {
                              boxShadow: (theme) => theme.tvp.cardHover,
                              transform: "translateY(-2px)",
                          },
                      }),
            }}
        >
            <Box
                sx={{
                    backgroundColor: (theme) => theme.tvp.surface,
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    borderRadius: (theme) => theme.tvp.radiusMedia,
                }}
            >
                <Box
                    component="img"
                    className="tvp-product-media"
                    src={common.adjustImageUrl(image_url, 600)}
                    alt=""
                    title={title}
                    loading={aboveFold ? "eager" : "lazy"}
                    decoding="async"
                    onLoad={() => setLoaded(true)}
                    sx={{
                        display: "block",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        opacity: loaded ? 1 : 0,
                        transition: "opacity 320ms ease, transform 260ms ease",
                        transitionDelay: `${revealDelay}ms, 0ms`,
                    }}
                />
            </Box>

            <Box padding={card ? "12px 2px 4px" : "12px 12px 16px"}>
                {card && brand && (
                    <Typography
                        fontSize="12.5px"
                        lineHeight={1.4}
                        color={(theme) => theme.tvp.textSubtle}
                        mb="4px"
                        title={brand}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {brand}
                    </Typography>
                )}
                <Typography
                    fontSize="14px"
                    fontWeight={600}
                    lineHeight="20px"
                    color={(theme) => theme.tvp.textPrimary}
                    title={title}
                    sx={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                        minHeight: "40px",
                    }}
                >
                    {title}
                </Typography>

                {description && !card && (
                    <Typography
                        fontSize="12.5px"
                        lineHeight={1.4}
                        color={(theme) => theme.tvp.textSubtle}
                        mt="4px"
                        sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                            overflow: "hidden",
                        }}
                    >
                        {description}
                    </Typography>
                )}

                <Box display="flex" alignItems="center" justifyContent="space-between" gap="10px" mt="8px">
                    {hasPrice && (
                        <Typography fontSize="14px" fontWeight={700}>
                            {onSale ? money(sale_price) : money(price)}
                            {onSale && (
                                <Typography
                                    component="span"
                                    fontSize="12.5px"
                                    fontWeight={400}
                                    color={(theme) => theme.tvp.textSubtle}
                                    sx={{ textDecoration: "line-through", marginLeft: "8px" }}
                                >
                                    {money(price)}
                                </Typography>
                            )}
                        </Typography>
                    )}
                    <QuickAdd product={item} />
                </Box>

                {!isLink && <Box
                    component="a"
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackClick}
                    sx={{
                        display: "block",
                        width: "100%",
                        marginTop: "14px",
                        boxSizing: "border-box",
                        border: 0,
                        cursor: "pointer",
                        textAlign: "center",
                        textDecoration: "none",
                        backgroundColor: (theme) => theme.tvp.cta,
                        color: (theme) => theme.tvp.ctaInk,
                        borderRadius: (theme) => theme.tvp.radiusCtl,
                        padding: "10px",
                        fontSize: "13.5px",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        transition: "opacity 120ms ease",
                        "&:hover": { opacity: 0.86, color: (theme) => theme.tvp.ctaInk },
                    }}
                >
                    Add to bag
                </Box>}
            </Box>
        </Box>
        </Box>
    );
};


export default ProductsCollectionItem;
